import { BPTInfo } from '@/models/bpt.model';
import { Action, Step } from './action.model';
import { Co2QuizAnswer, Co2QuizQuestion } from './co2-quiz.model';
import { InfoGraphic } from './info-graphic.model';
import { Initiative } from './initiative.model';
import { Story } from './story.model';
import { Category } from './category.model';
import { UserLabel } from './label.model';
import { Address } from './info.model';
import { QuizAnswer } from './quiz-question.model';
import { UserCommunity } from './community.model';
import { Goal } from './goal.model';

export interface User {
  id: number;
  createdDate?: string;
  updatedDate?: string;
  firstName: string;
  lastName: string;
  email: string;
  deviceID: string;
  language: string;
  level: UserLevel;
  address?: Address;
  stories: UserStory[];
  actions: UserAction[];
  steps: UserStep[];
  labels: UserLabel[];
  initiatives: UserInitiative[];
  infographics: UserInfoGraphic[];
  userCo2QuizAnswers: UserCo2QuizAnswer[];
  enablePushNotifications?: boolean;
  expoPushToken?: boolean;
  requestedDataRemoval?: boolean;
  requestedAccountDeletion?: boolean;
  notificationSettings: NotificationSetting[];
  bptInfo?: BPTInfo;
  interests?: Category[];
  communities: UserCommunity[];
  twoFactorEnabled?: boolean;
}

export enum NotificationType {
  Discounts = 'discounts',
  PopulairInitiatives = 'populair-initiatives',
  PopulairLearningItem = 'populair-learning-item',
  QuizStreak = 'quiz-streak',
}

interface NotificationSetting {
  notificationType: NotificationType;
  email: boolean;
  phone: boolean;
}

export enum UserLevel {
  Device = 'device',
  RegisteredUser = 'registered-user',
  ContentEditor = 'content-editor',
  Admin = 'admin',
}

export const firstnameRules = [
  (value: string) => {
    if (value) return true;

    return 'First name is required.';
  },
];

export const levelRules = [
  (value: string) => {
    if (value) return true;

    return 'Level is required.';
  },
];
export const lastnameRules = [
  (value: string) => {
    if (value) return true;

    return 'Last name is required.';
  },
];

export const getUserType = (user: User): string => {
  if (user.level === UserLevel.Device) {
    return 'Device';
  }
  if (user.level === UserLevel.RegisteredUser) {
    return 'User';
  }
  if (user.level === UserLevel.Admin) {
    return 'Admin';
  }
  if (user.level === UserLevel.ContentEditor) {
    return 'Editor';
  }

  return 'Unknown';
};

export interface UserAction {
  id: number;
  createdDate?: string;
  updatedDate?: string;
  user: User;
  action: Action;
  completed: boolean;
}

export interface UserStep {
  id: number;
  createdDate?: string;
  updatedDate?: string;
  user: User;
  step: Step;
  completed: boolean;
}

export interface UserStory {
  id: number;
  presentedDate: string;
  watchedDate?: string;
  completedDate: string;
  user: User;
  story: Story;
}

export interface UserCo2QuizAnswer {
  id: number;
  createdDate?: string;
  updatedDate?: string;
  question: Co2QuizQuestion;
  answer: Co2QuizAnswer | null;
  inputValue: number;
  user: User;
}

export interface SimpleUserCo2QuizAnswer {
  question: Partial<Co2QuizQuestion>;
  answer: Partial<Co2QuizAnswer> | null;
  inputValue: number;
}

export interface UserInfoGraphic {
  id: number;
  creationDate?: string;
  updatedDate?: string;
  user: User;
  infographic: InfoGraphic;
  completed: boolean;
}

export interface UserInitiative {
  id: number;
  creationDate?: string;
  updatedDate?: string;
  user: User;
  initiative: Initiative;
  completed: boolean;
}

export interface UserAnswer {
  id: number;
  answer: QuizAnswer;
}

export enum MessageType {
  Email = 'email',
  Notification = 'notification',
}

export interface UserMessage {
  id: number;
  creationDate: string;
  initiative: Initiative;
  discountInitiative: boolean;
  popularInitiative: boolean;
  story: Story;
  messageText: string;
  user: User;
  type: MessageType;
}

export interface UserGoal {
  id: number;
  creationDate: string;
  updatedDate: string;
  viewedDate: string;
  main: boolean;
  user: User;
  goal: Goal;
  completed: boolean;
}
